import { useState, useEffect } from 'react';
import { apiService } from '../../lib/api';
import type { SuccessStory } from '../../types';
import { Plus, Edit, Trash2, Eye, Loader2 } from 'lucide-react';
import { StoryEditor } from './StoryEditor';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

interface AdminPanelProps {
  onStorySelect: (storyId: string) => void;
  onStoryUpdate: () => void;
}

export function AdminPanel({ onStorySelect, onStoryUpdate }: AdminPanelProps) {
  const [stories, setStories] = useState<SuccessStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingStory, setEditingStory] = useState<SuccessStory | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState<string | null>(null);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllStories();
      if (response.success) {
        setStories(response.data);
      }
    } catch (error) {
      console.error('Error loading stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!storyToDelete) return;

    try {
      const response = await apiService.deleteStory(storyToDelete);
      if (response.success) {
        toast.success('Story deleted successfully');
        loadStories();
        onStoryUpdate();
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Failed to delete story');
    } finally {
      setDeleteDialogOpen(false);
      setStoryToDelete(null);
    }
  };

  const handleSaveComplete = () => {
    setEditingStory(null);
    setCreatingNew(false);
    loadStories();
    onStoryUpdate();
  };

  if (creatingNew || editingStory) {
    return (
      <StoryEditor
        story={editingStory}
        onSave={handleSaveComplete}
        onCancel={() => {
          setEditingStory(null);
          setCreatingNew(false);
        }}
      />
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Success Stories CMS</h1>
          <p className="text-muted-foreground">
            Create and manage customer success stories for your landing pages.
          </p>
        </div>
        <Button
          onClick={() => setCreatingNew(true)}
          className="bg-primary text-primary-foreground hover:opacity-90"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create New Story
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-20 bg-card rounded-xl border border-border">
          <h3 className="text-2xl font-bold text-foreground mb-2">No Stories Yet</h3>
          <p className="text-muted-foreground mb-6">
            Create your first customer success story to get started.
          </p>
          <Button onClick={() => setCreatingNew(true)} className="bg-primary text-primary-foreground">
            <Plus className="w-5 h-5 mr-2" />
            Create First Story
          </Button>
        </div>
      ) : (
        <div className="grid gap-6">
          {stories.map((story) => (
            <div
              key={story.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-foreground">{story.customerName}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        story.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : story.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {story.status}
                    </span>
                  </div>
                  <p className="text-muted-foreground mb-3">{story.headline}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="font-mono">{story.industry}</span>
                    <span>•</span>
                    <span className="font-mono">{story.companySize}</span>
                    <span>•</span>
                    <span className="font-mono">/{story.slug}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStorySelect(story.id)}
                    className="border-border"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingStory(story)}
                    className="border-border"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStoryToDelete(story.id);
                      setDeleteDialogOpen(true);
                    }}
                    className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the success story and all
              associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
